import { Sheet, Table, Typography } from '@healthinal/ui';
import type { ReactNode } from 'react';
import { TooltipCell } from './TooltipCell.tsx';

export type DataTableColumn<T> = {
  key: string;
  header: ReactNode;
  width?: number | string;
  align?: 'left' | 'center' | 'right';
  disableTooltip?: boolean;
  render: (row: T, index: number) => ReactNode;
};

type DataTableProps<T> = {
  columns: DataTableColumn<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string | number;
  isLoading?: boolean;
  emptyText?: string;
  loadingText?: string;
  onRowClick?: (row: T) => void;
  maxHeight?: string;
};

export function DataTable<T>({
  columns,
  rows,
  getRowKey,
  isLoading = false,
  emptyText = 'Keine Daten vorhanden.',
  loadingText = 'Lade Daten...',
  onRowClick,
  maxHeight = '60dvh',
}: DataTableProps<T>) {
  const colSpan = columns.length;
  const isEmpty = !isLoading && rows.length === 0;

  return (
    <Sheet sx={{ maxHeight, overflow: 'auto', width: '100%', minWidth: 0 }}>
      <Table
        stickyHeader
        hoverRow
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& th, & td': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingLeft: '8px',
            paddingRight: '8px',
            paddingTop: '6px',
            paddingBottom: '6px',
          },
        }}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} style={{ width: column.width, textAlign: column.align }}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={colSpan}>
                <Typography>{loadingText}</Typography>
              </td>
            </tr>
          ) : isEmpty ? (
            <tr>
              <td colSpan={colSpan}>
                <Typography>{emptyText}</Typography>
              </td>
            </tr>
          ) : (
            rows.map((row, index) => (
              <tr
                key={getRowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={onRowClick ? { cursor: 'pointer' } : undefined}>
                {columns.map((column) => {
                  const content = column.render(row, index);

                  if (column.disableTooltip) {
                    return (
                      <td key={column.key} style={{ textAlign: column.align }}>
                        {content}
                      </td>
                    );
                  }

                  return (
                    <TooltipCell key={column.key} style={{ textAlign: column.align }}>
                      {content}
                    </TooltipCell>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Sheet>
  );
}
